import PropTypes from 'prop-types';
import { useMemo, useCallback, memo } from 'react';
// -*- mode: jsx;-*-
// Defined in general.js
/* global Uri getURLParams getURLParam */

// Defined in JSP
/* global stats */

import { getIndex } from '../common/requestUtils.js';
import shuffleSearchResults from '../common/shuffleSearchResults.js';
import TabLayout from './TabLayout.js';
import useGiveWidget from '../common/giveWidget/useGiveWidget.js';
import useTabs from '../common/tabs/useTabs.js';
import useConstructor from '../common/react/useConstructor.js';
import { createChallengeSearchAPIURL } from '../search/searchApi.js';

import { Immutable } from '../../external/immutable.js';

const getTabName = function (url) {
    const params = getURLParams(url);
    if (params.active) return 'ACTIVE';
    if (params.historical) return 'HISTORICAL';
    if (params.supporters || params.updates) return 'UPDATES';
};
const PROJECTS_IN_PROGRESS = "Projects in progress";
const ELIGIBLE_PROJECTS = "Eligible projects";
const PROJECT_REQUESTS = "Project requests";

let GivingPageTabsController = function (props) {
    const [showGiveWidget, toggleGiveWidget] = useGiveWidget();

    const challenge = props.challenge;
    // We lay out partner page projects in rows of three,
    // so we need the per page number to be a multiple of three.
    const defaultPerPage = challenge.isPartnerPage ? 9 : 10;

    const validTabs = useMemo(() => {
        const challengeType = challenge.type;
        const max = defaultPerPage;
        const index = getIndex(location.href, max);
        const fundedOrCompletedProjects = (challenge.isPartnerPage) ? 'Completed projects' : 'Funded projects';
        const fundableProjects = getTabLabel(challenge);
        const challengeId = challenge.id;
        const isCampaign = challengeType === 'Campaign';
        let batchnames = undefined;
        let hiddenProjects = undefined;

        let jsonApiPath = 'common/json_challenge.html';
        if (challengeType === 'MatchingChallenge') {
            jsonApiPath = 'common/json_matching.html';
        } else if (challengeType === 'PromoCodeChallenge') {
            jsonApiPath = 'common/json_promocode.html';
        } else if (challengeType === 'GiftCodeChallenge') {
            jsonApiPath = 'common/json_giftcode.html';
        } else if (challengeType === 'District') {
            jsonApiPath = 'common/json_district_challenge.html';
        } else if (challengeType === 'Campaign') {
            jsonApiPath = 'common/json_feed.html'
            batchnames = challenge.batchnames;
            hiddenProjects = challenge.hiddenProjects;
        }

        return Immutable.OrderedMap().withMutations(function (map) {
            if ((stats.active > 0 && $('.donorschoose-hideProjects').length === 0)) {
                map.set('ACTIVE', Immutable.Map({
                    name: 'ACTIVE',
                    count: stats.active,
                    label: fundableProjects,
                    paramName: 'active',
                    requestURL: createChallengeSearchAPIURL(jsonApiPath, challengeId, undefined, false, index, max, isCampaign, batchnames, hiddenProjects, challenge.matchingIds),
                    indexParam: 'index',
                    isPartnerPage: challenge.isPartnerPage,
                }));
            }
            if (stats.funded > 0 && $('.donorschoose-hideCompletedProjects').length === 0) {
                map.set('HISTORICAL', Immutable.Map({
                    name: 'HISTORICAL',
                    count: stats.funded,
                    label: fundedOrCompletedProjects,
                    paramName: 'historical',
                    requestURL: createChallengeSearchAPIURL(jsonApiPath, challengeId, null, true, index, max, isCampaign, batchnames, hiddenProjects, null),
                    indexParam: 'index',
                    isPartnerPage: challenge.isPartnerPage,
                }));
            }
            if (stats.donors > 0 && $('.donorschoose-hideMembers').length === 0 && challengeType === 'Challenge') {
                map.set('UPDATES', Immutable.Map({
                    name: 'UPDATES',
                    count: stats.donors,
                    label: challenge.isPartnerPage ? 'Mobilized' : 'Supporters',
                    paramName: 'supporters',
                    requestURL: createChallengeSearchAPIURL(jsonApiPath, challengeId, 'supporters', undefined, index, max, false, undefined, undefined),
                    indexParam: 'supportersindex',
                    isPartnerPage: challenge.isPartnerPage,
                }));
            }
        });
    }, [defaultPerPage, challenge]);

    const convertToApiURL = useCallback(function (challengeURL, tab) {
        const maxParam = tab.get('maxParam');
        const perPage = defaultPerPage;
        const index = getIndex(challengeURL, perPage);
        const apiUri = new Uri(tab.get('requestURL'));

        const verifyShowPhotos = getURLParam(challengeURL, 'verifyShowPhotos');
        if (verifyShowPhotos) {
            apiUri.add('verifyShowPhotos', verifyShowPhotos);
        }

        if (maxParam) {
            apiUri.replace(maxParam, perPage);
        }
        apiUri.replace(tab.get('indexParam'), index);

        return apiUri.toString();
    }, [defaultPerPage]);

    const makeDataPromise = useCallback(function (challengeURL, tab) {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: convertToApiURL(challengeURL, tab),
                cache: true,
                dataType: 'json',
                success: (responseData) => {
                    const data = {
                        index: responseData.index,
                        max: responseData.max,
                        proposals: responseData.proposals,
                        totalProposals: (props.challenge.type !== 'Campaign' && tab.get('name') === 'HISTORICAL') ?
                            responseData.numFundedProposals : responseData.totalProposals,
                        supporters: responseData.supporters,
                    };
                    resolve(data);
                },
                error: () => {
                    reject('There was a problem loading more results. Please try again later.');
                },
            });
        });
    }, [convertToApiURL]);

    const shuffleData = useCallback(function (newData, tab) {
        if (challenge.shuffleProjects && tab.get('name') === 'ACTIVE') {
            newData.proposals = shuffleSearchResults(newData.proposals);
        }
    }, [challenge.shuffleProjects]);

    const { activateTab, changeURL, closeTab, activeTabName, loading, data, url, isMobile } = useTabs(
        makeDataPromise, validTabs, getTabName, shuffleData,
    );

    useConstructor(function () {
        if (validTabs.isEmpty()) return;

        // The tab specified in the URL takes priority
        const paramTab = getTabName(location.href);
        if (paramTab && validTabs.has(paramTab)) {
            changeURL(location.href);
        } else {
            // Use the configured default tab, if applicable
            let defaultTab = challenge.landingTab;
            if (!validTabs.has(defaultTab)) {
                // Otherwise, select the first available tab
                defaultTab = validTabs.keySeq().first();
            }

            activateTab(defaultTab);
        }
    });

    if (validTabs.isEmpty()) {
        return null; // Nothing to show
    }

    return <TabLayout {...props} loading={loading} isMobile={isMobile} url={url} activeTabName={activeTabName}
        data={data} validTabs={validTabs} showGiveWidget={showGiveWidget}
        isPartnerPage={challenge.isPartnerPage} perPage={defaultPerPage}
        activateTab={activateTab} changeURL={changeURL} closeTab={closeTab} toggleGiveWidget={toggleGiveWidget}
    />;
};

function getTabLabel(challenge) {
    if (challenge.type === "Campaign" ) {
        return (challenge.matchingIds !== null && challenge.matchingIds.length > 0) ? ELIGIBLE_PROJECTS : PROJECTS_IN_PROGRESS;
    }
    return challenge.isPartnerPage ? PROJECTS_IN_PROGRESS : PROJECT_REQUESTS;
}

GivingPageTabsController.propTypes = {
    challenge: PropTypes.object.isRequired,
    projectCardStyle: PropTypes.bool,
};
GivingPageTabsController = memo(GivingPageTabsController);
export default GivingPageTabsController;
